import { tokens } from "../../../theme";
import { useState } from "react";
import { Box, Button, Tooltip, Typography, useTheme } from "@mui/material";
import { Add } from "@mui/icons-material";
import { useHasPermissionState } from "../../../api/iam";
import { getHeaders } from "../../../utils/apiclient";
import ActionButton from "./ActionButton";
import ReleaseLabelRow from "./ReleaseLabelRow";
import useIamModal from "../../IamModal/UseIamModal";
import { releaseStyles } from "../styles";
import {
  MAX_VISIBLE_LABELS,
  PATCHABLE_RELEASE_STATUSES,
  actionText,
  formatAge,
  formatLabelDate,
  isDestructiveAction,
  labelCounts,
  nextPatchLabel,
  pickReleaseAction,
} from "../summaryHelpers";

// One release as a card: a tinted header strip carrying the release's own
// identity and actions, with its active labels stacked as rows underneath.
const ReleaseRow = ({
  release,
  app,
  handlePatchLabel,
  showReleaseStatusDialog,
  selected,
  onSelect,
}) => {
  const theme = useTheme();
  const { statusChip, summaryTable } = releaseStyles(tokens(theme.paletteId));

  const [showAllLabels, setShowAllLabels] = useState(false);

  const releasePath = `/apps/${app}/releases/${release?.name}`;
  const {
    hasPermission: canCreatePatch,
    isLoading: isCheckingPatchPermission,
  } = useHasPermissionState(`${releasePath}/release-labels`, "POST", "CREATE");

  // IAM scoped to this one release; the page header's button covers /releases
  // as a whole. The name is a dependency because useHasPermission only
  // re-checks when its deps change.
  const { IamModal, IamButton, canViewIamModal } = useIamModal(
    releasePath,
    [release?.name],
    summaryTable.cardIamButton
  );

  const highlightedLabels = (release?.releaseLabels || []).filter(
    (label) => label?.highlight
  );
  const visibleLabels = showAllLabels
    ? highlightedLabels
    : highlightedLabels.slice(0, MAX_VISIBLE_LABELS);
  const hiddenLabelCount = highlightedLabels.length - visibleLabels.length;
  const counts = labelCounts(release?.releaseLabels);

  const primaryAction = pickReleaseAction(release?.actionsAllowed);
  const canPatch =
    !primaryAction && PATCHABLE_RELEASE_STATUSES.includes(release?.status);
  const nextVersion = canPatch ? nextPatchLabel(release) : "";
  // The promoted action already has its own button in the cluster.
  const otherActions = (release?.actionsAllowed || []).filter(
    (action) => action !== primaryAction
  );

  const runReleaseAction = (action) =>
    showReleaseStatusDialog(
      `Perform action ${action} on release ${release?.name}`,
      `/${release?.name}`,
      {
        headers: { ...getHeaders(), Action: action },
      }
    );

  return (
    <Box sx={summaryTable.card(selected)}>
      <Box sx={summaryTable.cardHeader(selected)}>
        <Box sx={summaryTable.cardIdentity}>
          <Typography
            component="button"
            type="button"
            aria-haspopup="dialog"
            aria-expanded={Boolean(selected)}
            sx={summaryTable.cardName}
            onClick={() => onSelect?.(release)}
          >
            {release?.name}
          </Typography>
          <Typography component="span" sx={summaryTable.cardAge}>
            {formatAge(release?.dateCreated)}
          </Typography>
          <Box sx={summaryTable.cardChips}>
            <Box component="span" sx={statusChip(release?.status)}>
              {release?.status}
            </Box>
            {(release?.dynamicStatus || []).map((status) => (
              <Box key={status} component="span" sx={statusChip(status)}>
                {status}
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={summaryTable.historyStack}>
          <Typography sx={summaryTable.historyLine}>
            {counts
              ? `${counts.total} labels, ${counts.discarded} discarded`
              : "—"}
            {counts?.failed ? (
              <Box component="span" sx={summaryTable.historyFailed}>
                {`, ${counts.failed} failed validation${
                  counts.failed === 1 ? "" : "s"
                }`}
              </Box>
            ) : null}
          </Typography>

          {false && release?.dateCreated ? (
            <Typography sx={summaryTable.cardCreatedDate}>
              {`Created ${formatLabelDate(release.dateCreated)}`}
            </Typography>
          ) : null}
        </Box>

        <Box sx={summaryTable.cardActions}>
          {primaryAction ? (
            <ActionButton
              action={primaryAction}
              path={releasePath}
              text={actionText(primaryAction, release?.status)}
              // Only CREATE actually makes something; MARK_CREATED just records
              // a state, so it keeps the plain label.
              variant={primaryAction === "CREATE" ? "contained" : "outlined"}
              startIcon={primaryAction === "CREATE" ? <Add /> : undefined}
              sx={
                primaryAction === "CREATE"
                  ? summaryTable.createButton
                  : summaryTable.releaseActionButton(false)
              }
              onClick={() => runReleaseAction(primaryAction)}
            />
          ) : null}
          {canPatch ? (
            <Tooltip
              title={
                <Typography>
                  {isCheckingPatchPermission
                    ? "Checking permissions…"
                    : nextVersion
                    ? `Create patch ${nextVersion} for ${release?.name}`
                    : `Create a new patch for ${release?.name}`}
                </Typography>
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Add />}
                  sx={summaryTable.patchButton}
                  disabled={isCheckingPatchPermission}
                  onClick={() => handlePatchLabel(release, canCreatePatch)}
                >
                  Patch
                </Button>
              </span>
            </Tooltip>
          ) : null}
          {otherActions.map((action) => (
            <ActionButton
              key={action}
              action={action}
              path={releasePath}
              text={actionText(action, release?.status)}
              sx={summaryTable.releaseActionButton(isDestructiveAction(action))}
              onClick={() => runReleaseAction(action)}
            />
          ))}
          {canViewIamModal ? (
            <>
              <Box sx={summaryTable.actionDivider} />
              {IamButton}
            </>
          ) : null}
        </Box>
      </Box>

      {visibleLabels.length ? (
        visibleLabels.map((releaseLabel) => (
          <ReleaseLabelRow
            key={releaseLabel?.name}
            release={release}
            releaseLabel={releaseLabel}
            app={app}
            showReleaseStatusDialog={showReleaseStatusDialog}
          />
        ))
      ) : (
        <Typography sx={summaryTable.emptyLabels}>
          {release?.status === "CREATING"
            ? "Creation in progress"
            : "No labels yet"}
        </Typography>
      )}

      {hiddenLabelCount > 0 ? (
        <Box sx={summaryTable.showAllRow}>
          <Button
            size="small"
            sx={summaryTable.showAllButton}
            onClick={() => setShowAllLabels(true)}
          >
            {`Show all ${highlightedLabels.length}`}
          </Button>
        </Box>
      ) : null}

      {IamModal}
    </Box>
  );
};

export default ReleaseRow;
